import type {
  AVRoomState,
  FormattedChatMessage,
  MattermostChannel,
  MessageReaction,
  PollDetails,
  PollQuestion,
  PollsChannel,
  RawMessage
} from './types';

import loggerService from '@/services/logger.service';
import { deepClone } from '@/utils/objectUtil';

export type PropType<TObj, TProp extends keyof TObj> = TObj[TProp];

const mutations = {
  updateJitterDelay(state: AVRoomState, delay: number) {
    state.jitterdelay = Math.floor(delay * Math.random());
  },
  setP2PChannelBeingCreated(state: AVRoomState, value: boolean) {
    state.isP2PChannelBeingCreated = value;
  },
  setNumUnread(state: AVRoomState, payload: { channel_id: string; res: { msg_count: number } }) {
    const chat = state.chats.find(it => it.channel.id === payload.channel_id);
    if (chat) {
      chat.channel.total_unread = payload.res?.msg_count ?? 0;
    }
  },
  setChatLoadingState(
    state: AVRoomState,
    payload: {
      channel_id: string;
      status: PropType<MattermostChannel, 'loadingState'>;
    }
  ) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);
    if (chat) {
      chat.channel.loadingState = payload.status;
    }
  },
  addChat(state: AVRoomState, chat: PropType<AVRoomState, 'chats'>[0]) {
    state.chats = [chat].concat(
      state.chats
        .map(c => {
          c.scrolled = false;
          c.hasunseen = false;
          return c;
        })
        .filter(c => c.channel.id !== chat.channel.id)
    );
  },
  setIsBackstage(state: AVRoomState, payload: boolean) {
    state.isBackstageInDiscRoom = payload;
  },
  addChatWithoutDock(state: AVRoomState, chat: PropType<AVRoomState, 'chats'>[0]) {
    const existingChat = state.chats.find(c => c.channel.id === chat.channel.id);
    if (!existingChat) {
      chat.scrolled = false;
      chat.hasunseen = false;
      state.chats = state.chats.concat(chat);
    }
  },
  initPublicChat(state: AVRoomState, channelId: string) {
    const existingchat = state.chats.find(chat => chat.channel.id === channelId);
    if (!existingchat) {
      type chat = PropType<AVRoomState, 'chats'>[0];
      const newchat: chat = {
        channel: {
          id: channelId,
          messages: [],
          participants: [],
          loadingState: 'loading',
          type: 'O',
          name: '',
          total_unread: 0
        },
        scrolled: false,
        hasunseen: false
      };
      state.chats = [newchat, ...state.chats];
    }
  },
  setChannelStatus(state: AVRoomState, payload: { channelId: string; status: 'loading' | 'loaded' | 'left' }) {
    state.channel_loading = {
      ...state.channel_loading,
      [payload.channelId]: payload.status
    };
  },
  setAllChannelsToLeft(state: AVRoomState) {
    const channels = Object.keys(state.channel_loading);
    const channel_loading: Record<string, 'loading' | 'loaded' | 'left'> = {};
    for (let i = 0; i < channels.length; i++) {
      const channel_id = channels[i];
      channel_loading[channel_id] = 'left';
    }
    state.channel_loading = channel_loading;
  },
  clearLocalMessages(state: AVRoomState, channel_id: string) {
    state.chats = state.chats.map(chat => {
      if (chat.channel.id !== channel_id) {
        return chat;
      }
      const newchat: PropType<AVRoomState, 'chats'>[0] = {
        ...chat,
        channel: {
          participants: [],
          messages: [],
          loadingState: 'loaded',
          id: chat.channel.id,
          type: chat.channel.type,
          name: chat.channel.name
        }
      };
      return newchat;
    });
  },
  clearPolls(state: AVRoomState, channel_id: string) {
    if (state.polls && state.polls.id === channel_id) {
      state.polls = null;
    }
  },
  addMessages(state: AVRoomState, payload: { posts: any[]; channel_id: string; updateUnread: boolean }) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);

    if (!chat) {
      loggerService.info("Not adding chat message as channel doesn't exist yet!");
      return;
    }

    let maxMessagesLength = chat.channel.messages.length;
    if (maxMessagesLength <= 20) {
      maxMessagesLength = 20;
    }

    const noOfMessagesReceived = payload.posts.length;

    let messages = chat.channel.messages.filter(it => !payload.posts.find(p => p?.props?.nonce === it?.props?.nonce));
    const repeatedMessagesCount = chat.channel.messages.length - messages.length;

    messages = messages.concat(payload.posts);

    //@ts-ignore
    const previousMessageID = messages?.at(-(maxMessagesLength + 1))?.id;

    chat.channel.messages = messages.slice(-maxMessagesLength);

    state.chatStateInvalidated = Date.now();

    const numMessagesAdded = Math.max(0, noOfMessagesReceived - repeatedMessagesCount);

    if (numMessagesAdded > 0 && chat.scrolled) {
      chat.hasunseen = true;
    }

    if (previousMessageID) {
      chat.channel.previousMessageID = previousMessageID;
    }

    if (payload.updateUnread) {
      chat.channel.total_unread = chat.channel.total_unread || 0;
      chat.channel.total_unread += numMessagesAdded;
    }
  },
  removeMessageByNonce(
    state: AVRoomState,
    payload: {
      nonce: string;
      channel_id: string;
    }
  ) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);

    if (!chat) {
      return;
    }

    chat.channel.messages = chat.channel.messages.filter(message => message.props?.nonce !== payload.nonce);
  },
  deletePinnedMessage(state: AVRoomState, payload: { id: string; channel_id: string }) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);
    if (chat && chat.channel.pinned_messages) {
      chat.channel.pinned_messages = chat.channel.pinned_messages.filter(it => it.id !== payload.id);
    } else {
      loggerService.warn(
        'Could not find chat while trying to delete pinned message: ' + payload.channel_id + ':' + payload.id
      );
    }
    // Invalidate the getters at the chat level
    state.chats = state.chats.map(chat => chat);
  },
  addPinnedMessages(state: AVRoomState, payload: { posts: any[]; channel_id: string }) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);
    if (chat) {
      chat.channel.pinned_messages = payload.posts;
    } else {
      loggerService.warn(
        'Could not find chat while trying to add pinned message: ' +
          payload.channel_id +
          ':' +
          JSON.stringify(payload.posts)
      );
    }
    // Invalidate the getters at the chat level
    state.chats = state.chats.map(chat => chat);
  },
  addMessagesToBeginning(state: AVRoomState, payload: { posts: any[]; channel_id: string }) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);
    if (chat) {
      chat.channel.messages = [...payload.posts, ...chat.channel.messages];
    }
  },
  replaceMessages(state: AVRoomState, payload: { posts: any[]; channel_id: string }) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);

    if (!chat) {
      return;
    }

    const sendErrorMessages = chat.channel.messages.filter(m => m.error === 'send_message_error');

    chat.channel.messages = payload.posts.concat(sendErrorMessages);
  },
  addReaction(state: AVRoomState, payload: { reaction: MessageReaction; channel_id: string }) {
    const emojiName = payload.reaction.emoji_name;

    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);

    if (!chat?.channel?.messages?.length) {
      return;
    }

    let editedMessage: FormattedChatMessage | undefined;

    const messages = chat.channel.messages.map(message => {
      if (message.id !== payload.reaction.post_id) {
        return message;
      }
      message.reactions = message.reactions || {};
      message.error = undefined;

      const existingReactionIndex = message.reactions?.[emojiName]?.findIndex(
        reaction => reaction.user_id === payload.reaction.user_id
      );

      if (existingReactionIndex > -1) {
        if (payload.reaction.serverResponseState === 'error') {
          message.reactions[emojiName] = message.reactions[emojiName].filter(
            reaction => reaction.user_id !== payload.reaction.user_id
          );
          message.error = 'add_emoji_error';
        }

        if (payload.reaction.serverResponseState === 'success') {
          message.reactions[emojiName][existingReactionIndex] = payload.reaction;
        }

        editedMessage = message;
        return editedMessage;
      }

      editedMessage = {
        ...message,
        reactions: {
          ...message.reactions,
          [emojiName]: [...(message.reactions?.[emojiName] || []), payload.reaction]
        }
      };
      return editedMessage;
    });

    chat.channel.messages = messages;

    if (chat.channel.pinned_messages) {
      chat.channel.pinned_messages = chat.channel.pinned_messages.map(p => {
        if (p.id === payload.reaction.post_id && editedMessage) {
          p.reactions = editedMessage.reactions;
        }
        return p;
      });
    }
  },
  deleteReaction(state: AVRoomState, payload: { reaction: MessageReaction; channel_id: string }) {
    const emojiName = payload.reaction.emoji_name;

    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);

    if (!chat?.channel?.messages?.length) {
      return;
    }

    let editedMessage: FormattedChatMessage | undefined;

    const messages = chat.channel.messages.map(message => {
      if (message.id !== payload.reaction.post_id) {
        return message;
      }
      message.reactions = message.reactions || {};
      message.error = undefined;

      const filteredReactions = message.reactions[emojiName]?.filter(reaction => {
        return reaction.user_id !== payload.reaction.user_id;
      });

      if (payload.reaction.serverResponseState === 'error') {
        filteredReactions.push(payload.reaction);
        message.error = 'remove_emoji_error';
      }

      editedMessage = {
        ...message,
        reactions: {
          ...message.reactions,
          [emojiName]: filteredReactions
        }
      };

      return editedMessage;
    });

    chat.channel.messages = messages;

    if (chat.channel.pinned_messages) {
      chat.channel.pinned_messages = chat.channel.pinned_messages.map(p => {
        if (p.id === payload.reaction.post_id && editedMessage) {
          p.reactions = editedMessage.reactions;
        }
        return p;
      });
    }
  },
  nextPostID(state: AVRoomState, payload: { id: string; channel_id: string }) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);
    if (chat) {
      chat.channel = {
        ...chat.channel,
        nextMessageID: payload.id
      };
    }
  },
  prevPostID(state: AVRoomState, payload: { id: string; channel_id: string }) {
    const chat = state.chats.find(chat => chat.channel.id === payload.channel_id);
    if (chat) {
      chat.channel = {
        ...chat.channel,
        previousMessageID: payload.id
      };
    }
  },
  initPoll(state: AVRoomState, pollChannelID: string) {
    const polls: PropType<AVRoomState, 'polls'> = {
      id: pollChannelID,
      details: {
        questions: {},
        currentOpenedPollID: '',
        currentPublishedPollID: '',
        answerIDToQuestionIDMap: {}
      },
      pollState: 'loaded',
      timeToFetchNextPollDetails: 6 // can be picked from the event details
    };
    state.polls = polls;
  },
  pollInitError(state: AVRoomState) {
    if (state.polls) {
      state.polls.pollState = 'initializationerror';
    }
  },
  pollsLoadingError(state: AVRoomState) {
    if (state.polls) {
      state.polls.pollState = 'loadingerror';
    }
  },
  changePollState(state: AVRoomState, payload: PropType<PollsChannel, 'pollState'>) {
    if (state.polls) {
      state.polls.pollState = payload;
    }
  },
  handleNewPollPost(state: AVRoomState, post: RawMessage) {
    if (state.polls?.details) {
      if (post.props?.type === 'OPENED_POLL') {
        state.polls.details.currentOpenedPollID = post.message;
      } else if (post.props?.type === 'PUBLISHED_POLL') {
        state.polls.details.currentPublishedPollID = post.message;
      }
    }
  },
  changePollAnswerLoadingState(
    state: AVRoomState,
    payload: {
      answerID: string;
      loading: boolean;
    }
  ) {
    const answer = getAnswerFromStore(state, payload.answerID);
    if (answer) {
      answer.loading = payload.loading || false;
    }
  },
  removeVoteForAnswerInLocalState(state: AVRoomState, answerID: string) {
    const question = getQuestionForAnswerFromStore(state, answerID);
    const answer = question?.answers[answerID];
    if (question && question.id && answer && answer.id) {
      const newVotedAnswers = (question.votedAnswers || []).filter(votedAnsID => {
        return votedAnsID !== answerID;
      });
      answer.votes = Math.max(0, answer.votes - 1);
      question.totalVotes = Math.max(0, question.totalVotes - 1);
      question.votedAnswers = [...newVotedAnswers];
    }
  },
  addVoteForAnswerInLocalState(state: AVRoomState, answerID: string) {
    const question = getQuestionForAnswerFromStore(state, answerID);
    const answer = question?.answers[answerID];
    if (question?.id && answer?.id) {
      question.votedAnswers = [...(question.votedAnswers || []), answerID];
      answer.votes = answer.votes + 1;
      question.totalVotes = question.totalVotes + 1;
    }
  },
  updateAnswerCountForPoll(
    state: AVRoomState,
    payload: {
      answersToVotesMap: Record<string, number>;
      questionID: string;
      isAsyncFlow: boolean;
    }
  ) {
    try {
      loggerService.info(`Updating the result count using ${payload.isAsyncFlow ? 'async' : 'sync'} poll flow `);
      if (state.polls?.details?.questions && payload.answersToVotesMap && payload.questionID) {
        const questionDetails = state.polls?.details.questions[payload.questionID];
        if (!questionDetails) {
          return;
        }
        const newQuestionDetails = deepClone(questionDetails);
        newQuestionDetails.totalVotes = 0;
        Object.keys(newQuestionDetails?.answers).forEach(answerID => {
          const votesCount = payload.answersToVotesMap[answerID] || 0;
          newQuestionDetails.totalVotes = newQuestionDetails.totalVotes + votesCount;
          newQuestionDetails.answers[answerID].votes = votesCount;
        });
        state.polls.details.questions[payload.questionID] = newQuestionDetails;
      }
    } catch (err) {
      loggerService.error('Error updating poll answer count in store!', err);
    }
  },
  setBroadcastCounts(state: AVRoomState, payload: { id: string; count: number }) {
    state.broadcastOnlineCount = {
      ...state.broadcastOnlineCount,
      [payload.id]: payload.count
    };
  },
  setIncomingVideoCall(state: AVRoomState, payload: { roomId: string; callingUser: any } | null) {
    state.incomingVideoCall = payload;
  },
  saveActiveBroadcastOnliner(state: AVRoomState, onliner: any) {
    if (onliner) {
      if (state.activeBroadcastOnliner) {
        clearInterval(state.activeBroadcastOnliner);
      }
      state.activeBroadcastOnliner = onliner;
    }
  },
  removeActiveBroadcastOnliner(state: AVRoomState) {
    if (state.activeBroadcastOnliner) {
      clearInterval(state.activeBroadcastOnliner);
      state.activeBroadcastOnliner = null;
    }
  },
  addToAttendeeDiscussionRooms(state: AVRoomState, payload: { userId: string; roomId: string }) {
    state.attendeesPrivateDiscussionRooms = [
      ...state.attendeesPrivateDiscussionRooms,
      { [payload.userId]: payload.roomId }
    ];
  },
  stopCaptionVisibilityInterval(state: AVRoomState) {
    if (state.captionVisibilityInterval) {
      clearInterval(state.captionVisibilityInterval);
    }
  },
  deletePollQuestionDetails(state: AVRoomState, post: RawMessage) {
    if (
      state.polls?.id !== post?.channel_id ||
      post?.props?.type !== 'QUESTION' ||
      !(state.polls.details && state.polls.details.questions && post.id in state.polls.details.questions)
    ) {
      return;
    }
    const pollDetails = JSON.parse(JSON.stringify(state.polls.details));
    const question = pollDetails?.questions[post.id];
    const answersIDsForQuestion = Object.keys(question.answers);
    delete pollDetails.questions[post.id];
    answersIDsForQuestion.forEach((answerID: string) => {
      delete pollDetails.answerIDToQuestionIDMap[answerID];
    });
    state.polls.details = pollDetails;
  },
  deletePollAnswer(state: AVRoomState, post: RawMessage) {
    if (state.polls?.id !== post?.channel_id || post?.props?.type !== 'ANSWER') {
      return;
    }

    const pollDetails: PollDetails = JSON.parse(JSON.stringify(state.polls.details));
    const questionId = pollDetails.answerIDToQuestionIDMap[post.id];

    if (!questionId) {
      return;
    }

    const question = pollDetails.questions[questionId];

    if (!question) {
      return;
    }

    const currentAnswerDetails = question.answers[post.id];

    if (!currentAnswerDetails) {
      return;
    }

    question.totalVotes = question.totalVotes - currentAnswerDetails.votes;
    delete question.answers[post.id];
    delete pollDetails.answerIDToQuestionIDMap[post.id];
    state.polls.details = pollDetails;
  },

  toggleSpeakerActivityMessages(state: AVRoomState) {
    state.showSpeakerActivityMessages = !state.showSpeakerActivityMessages;
  }
};

function getQuestionForAnswerFromStore(state: AVRoomState, answerID: string) {
  if (state.polls?.details?.answerIDToQuestionIDMap && answerID) {
    const questionID = state.polls.details.answerIDToQuestionIDMap[answerID];
    const questionDetails: PollQuestion = state.polls.details.questions[questionID];
    return questionDetails;
  }
}

function getAnswerFromStore(state: AVRoomState, answerID: string) {
  const questionDetails = getQuestionForAnswerFromStore(state, answerID);
  if (questionDetails) {
    const answer = questionDetails.answers[answerID];
    return answer;
  }
}

export type AVMutations = typeof mutations;

export default mutations;
